import { MultiTypeAverageWindow } from '../utils/MultiTypeAverageWindow';
import {
  type EngagedRange,
  LayoutManager,
  type LayoutManagerOptions,
  type RVLayout,
  type RVLayoutInfo,
} from './LayoutManager';

const DEFAULT_ITEM_SIZE = 200;
const AVERAGE_WINDOW_CAPACITY = 10;
// Tiny tolerance so floating-point sizes summing to boundedSize still fit
// in a row. Matches upstream RVGridLayoutManagerImpl.checkBounds.
const ROW_FIT_EPSILON = 0.9;

export interface GridLayoutManagerOptions extends LayoutManagerOptions {
  numColumns: number;
  /**
   * Mutator-style API matching upstream FlashList (RVLayoutManager.getSpan):
   * assign `layout.span` to widen the cell to span N columns (or, in
   * horizontal mode, N rows). Returns void. Called once per item per
   * relayout pass; called more often than `processLayoutInfo` so keep it
   * cheap.
   */
  overrideItemLayout?: (
    layout: { span?: number },
    index: number,
    maxColumns: number,
  ) => void;
}

interface GridLayoutCell extends RVLayout {
  /** Span in tracks, after clamping to `numColumns`. */
  span: number;
}

/**
 * Grid layout. Works in both axes:
 *   - vertical: `numColumns` columns along x, items accumulate along y.
 *   - horizontal: `numColumns` rows along y, items accumulate along x.
 * `boundedSize` is the perpendicular-to-scroll dimension; `trackSize` is
 * `boundedSize / numColumns`.
 */
export class GridLayoutManager extends LayoutManager {
  private layouts: GridLayoutCell[] = [];
  /** Primary-axis measurement: height for vertical, width for horizontal. */
  private readonly measuredSizes = new Map<number, number>();
  private readonly averageWindow: MultiTypeAverageWindow;
  private readonly defaultItemSize: number;
  private readonly numColumns: number;
  private readonly overrideItemLayout?: (
    layout: { span?: number },
    index: number,
    maxColumns: number,
  ) => void;
  private readonly spanProbe: { span?: number } = {};

  constructor(options: GridLayoutManagerOptions) {
    super(options);
    this.numColumns = Math.max(1, options.numColumns);
    this.defaultItemSize = options.defaultItemSize ?? DEFAULT_ITEM_SIZE;
    this.overrideItemLayout = options.overrideItemLayout;
    this.averageWindow = new MultiTypeAverageWindow(
      options.averageWindowCapacity ?? AVERAGE_WINDOW_CAPACITY,
      this.defaultItemSize,
    );
    this.recompute();
  }

  processLayoutInfo(infos: RVLayoutInfo[]): void {
    // Reject measurements before the scroller has a real viewport — see
    // LinearLayoutManager.processLayoutInfo for the rationale.
    if (this.windowSize.width === 0 || this.windowSize.height === 0) return;
    for (const { index, dimensions } of infos) {
      if (index < 0 || index >= this.count) continue;
      const reported = this.horizontal ? dimensions.width : dimensions.height;
      // Drop zeros — they happen mid-mount before the cell has laid out, and
      // feeding them in would self-perpetuate (size=0 → renders at 0 → RO
      // reports 0 → loops).
      if (reported === 0) continue;
      const previous = this.measuredSizes.get(index);
      if (previous === reported) continue;
      this.measuredSizes.set(index, reported);
      this.averageWindow.addValue(reported, this.typeOf(index));
    }
    this.recompute();
  }

  getLayout(index: number): RVLayout {
    const cell = this.layouts[index];
    if (cell) return cell;
    // Defensive fallback — caller asked for an index outside the current
    // count.
    return {
      x: 0,
      y: 0,
      width: this.horizontal ? this.defaultItemSize : this.trackSize(),
      height: this.horizontal ? this.trackSize() : this.defaultItemSize,
      isMeasured: false,
    };
  }

  getItemHeight(index: number): number {
    return this.layouts[index]?.height ?? this.defaultItemSize;
  }

  getContentSize(): { width: number; height: number } {
    if (this.layouts.length === 0) {
      return {
        width: this.horizontal ? 0 : this.windowSize.width,
        height: this.horizontal ? this.windowSize.height : 0,
      };
    }
    const last = this.layouts[this.layouts.length - 1];
    if (this.horizontal) {
      return {
        width: last.x + last.width,
        height: this.windowSize.height,
      };
    }
    return {
      width: this.windowSize.width,
      height: last.y + last.height,
    };
  }

  findIndicesInRange(
    rangeStart: number,
    rangeEnd: number,
  ): EngagedRange | null {
    if (this.count === 0) return null;
    let start = -1;
    let end = -1;
    for (let index = 0; index < this.layouts.length; index++) {
      const cell = this.layouts[index];
      const primaryStart = this.horizontal ? cell.x : cell.y;
      const primaryEnd =
        primaryStart + (this.horizontal ? cell.width : cell.height);
      if (primaryEnd > rangeStart && primaryStart < rangeEnd) {
        if (start === -1) start = index;
        end = index;
      } else if (primaryStart >= rangeEnd) {
        break;
      }
    }
    if (start === -1) {
      return { start: this.count - 1, end: this.count - 1 };
    }
    return { start, end };
  }

  shiftMeasurements(offset: number): void {
    if (offset === 0) return;
    const entries = Array.from(this.measuredSizes.entries());
    this.measuredSizes.clear();
    for (const [oldIndex, size] of entries) {
      const newIndex = oldIndex + offset;
      if (newIndex >= 0 && newIndex < this.count) {
        this.measuredSizes.set(newIndex, size);
      }
    }
    this.recompute();
  }

  protected override onItemCountChanged(): void {
    for (const index of [...this.measuredSizes.keys()]) {
      if (index >= this.count) this.measuredSizes.delete(index);
    }
    this.recompute();
  }

  protected override onWindowSizeChanged(): void {
    this.recompute();
  }

  /** Perpendicular-to-scroll dimension assigned to one track (one span unit). */
  private trackSize(): number {
    const boundedSize = this.horizontal
      ? this.windowSize.height
      : this.windowSize.width;
    return boundedSize / this.numColumns;
  }

  private boundedSecondary(): number {
    return this.horizontal ? this.windowSize.height : this.windowSize.width;
  }

  private resolveSpan(index: number): number {
    if (!this.overrideItemLayout) return 1;
    this.spanProbe.span = undefined;
    this.overrideItemLayout(this.spanProbe, index, this.numColumns);
    return Math.min(this.spanProbe.span ?? 1, this.numColumns);
  }

  private primarySizeFor(index: number): number {
    const measured = this.measuredSizes.get(index);
    if (measured != null) return measured;
    return this.averageWindow.getCurrentValue(this.typeOf(index));
  }

  private recompute(): void {
    const trackSize = this.trackSize();
    const boundedSize = this.boundedSecondary();
    const cells: GridLayoutCell[] = new Array(this.count);

    let rowStart = 0;
    // primaryStart = y for vertical, x for horizontal (the scroll-axis edge
    // of the current row).
    let primaryStart = 0;
    // secondaryCursor = x for vertical, y for horizontal (track position).
    let secondaryCursor = 0;
    let rowMaxPrimary = 0;

    const finalizeRow = (lastIndex: number) => {
      // Normalize every item in the row to the row's max primary size.
      for (let i = rowStart; i <= lastIndex; i++) {
        if (this.horizontal) {
          cells[i].width = rowMaxPrimary;
        } else {
          cells[i].height = rowMaxPrimary;
        }
      }
    };

    for (let index = 0; index < this.count; index++) {
      const span = this.resolveSpan(index);
      const secondarySize = trackSize * span;
      const primarySize = this.primarySizeFor(index);

      if (secondaryCursor + secondarySize > boundedSize + ROW_FIT_EPSILON) {
        finalizeRow(index - 1);
        primaryStart += rowMaxPrimary;
        rowMaxPrimary = 0;
        secondaryCursor = 0;
        rowStart = index;
      }

      cells[index] = this.horizontal
        ? {
            x: primaryStart,
            y: secondaryCursor,
            width: primarySize,
            height: secondarySize,
            isMeasured: this.measuredSizes.has(index),
            span,
          }
        : {
            x: secondaryCursor,
            y: primaryStart,
            width: secondarySize,
            height: primarySize,
            isMeasured: this.measuredSizes.has(index),
            span,
          };

      if (primarySize > rowMaxPrimary) rowMaxPrimary = primarySize;
      secondaryCursor += secondarySize;
    }

    if (this.count > 0) {
      finalizeRow(this.count - 1);
    }

    this.layouts = cells;
  }
}
